import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { Contact } from '../../models/contact.model';
import { ContactService } from './contact.service';
import { FermeStateService } from './ferme-state.service';

export function normTexte(s: string | undefined | null): string {
  return (s || '').trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

/**
 * Contacts de la ferme chargés une fois pour tous les sélecteurs (quelques centaines au plus),
 * avec les contacts récemment utilisés en tête.
 */
@Injectable({ providedIn: 'root' })
export class ContactCacheService {
  private cache$: Observable<Contact[]> | null = null;
  private liste$ = new BehaviorSubject<Contact[]>([]);
  private readonly CLE_RECENTS = 'bay.contacts.recents';

  constructor(private contactService: ContactService, private fermeState: FermeStateService) {}

  tous(): Observable<Contact[]> {
    if (!this.cache$) {
      this.cache$ = this.contactService.getAll(this.fermeState.fermeId).pipe(
        map(c => (c || []).filter(x => !(x as any).archive)),
        tap(c => this.liste$.next(c)),
        catchError(() => { this.cache$ = null; return of([] as Contact[]); }),
        shareReplay(1)
      );
    }
    return this.cache$;
  }

  get snapshot(): Contact[] { return this.liste$.getValue(); }

  /** Clients (type CLIENT ou LES_DEUX) ou fournisseurs selon le sens */
  duType(contacts: Contact[], type?: 'CLIENT' | 'FOURNISSEUR'): Contact[] {
    return !type ? contacts : contacts.filter(c => c.type === type || c.type === 'LES_DEUX');
  }

  creer(nom: string, type: 'CLIENT' | 'FOURNISSEUR', telephone?: string): Observable<Contact> {
    return this.contactService.create({ fermeId: this.fermeState.fermeId, nom: nom.trim(), type, telephone } as Contact).pipe(
      tap(c => this.ajouter(c))
    );
  }

  ajouter(c: Contact): void {
    const l = [...this.snapshot.filter(x => x.id !== c.id), c];
    this.liste$.next(l);
    this.cache$ = of(l).pipe(shareReplay(1));
  }

  invalider(): void { this.cache$ = null; }

  trouverParNom(nom: string, type?: 'CLIENT' | 'FOURNISSEUR'): Contact | undefined {
    const n = normTexte(nom);
    return n ? this.duType(this.snapshot, type).find(c => normTexte(c.nom) === n) : undefined;
  }

  marquerUtilise(id?: number): void {
    if (!id) return;
    try {
      const r = this.recents().filter(x => x !== id);
      r.unshift(id);
      localStorage.setItem(this.CLE_RECENTS, JSON.stringify(r.slice(0, 20)));
    } catch { /* stockage indisponible : sans conséquence */ }
  }

  recents(): number[] {
    try { return JSON.parse(localStorage.getItem(this.CLE_RECENTS) || '[]'); } catch { return []; }
  }
}
